/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
// adopt
import { adopt } from 'react-adopt';
// graphQL
import gql from 'graphql-tag';
// global variables
import { defaultNumberOfRows as DEFAULT_NUMBER_OF_ROWS } from 'config';
// our components
import qs, { getQueryString } from 'helpers/queryStrings';
import RouterContext from 'helpers/routerContext';
import { Query } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator';
import NotificationCategoryList from './List';

/**
 * Query to fetch notification categories from api
 */
export const NOTIFICATION_CATEGORIES_QUERY = gql`
  query NotificationCategoryList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $id: ID
    $name: String
  ) {
    adminNotificationCategories(
      first: $first
      after: $after
      last: $last
      before: $before
      id: $id
      name: $name
    ) @connection(key: "adminNotificationCategories") {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          name
          order
          description
        }
      }
    }
  }
  ${PageInfo}
`;

/**
 * Default filters in NotificationCategoryList
 */
const defaultFilters = {
  id: undefined,
  name: undefined,
};

/**
 * @class
 * @extends Component
 */
class NotificationCategoryListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { initialFilters } = props;
    const {
      numberOfRowsSelected,
      direction,
      cursor,
      ...initFilters
    } = initialFilters;

    this.state = {
      filters: {
        ...defaultFilters,
        ...initFilters,
      },
      numberOfRowsSelected:
        parseInt(numberOfRowsSelected, 0) || DEFAULT_NUMBER_OF_ROWS,
      pagination: {
        direction: direction || null,
        cursor: cursor || undefined,
      },
    };

    this.Compose = adopt({
      queryNotificationCategories: this.notificationCategories,
    });
  }

  /**
   * @description
   * Query callback to compose
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  notificationCategories = ({ render }) => {
    const { filters, pagination, numberOfRowsSelected } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };
    return (
      <Query
        query={NOTIFICATION_CATEGORIES_QUERY}
        variables={{
          ...filters,
          ...paginationVariables,
        }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, fetchMore }) => {
          this.fetchMore = fetchMore;
          return render({ data, loading, fetchMore });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} filters - Filters to apply.
   */
  handleFilter = filters => {
    const history = this.context;
    this.setState({
      filters: {
        ...filters,
      },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...filters,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  render() {
    const { filters, numberOfRowsSelected, pagination } = this.state;
    return (
      <this.Compose>
        {({ queryNotificationCategories: { data, loading } }) => (
          <NotificationCategoryList
            data={data}
            loading={Object.keys(data).length === 0}
            fullLoading={loading}
            numberOfRowsSelected={numberOfRowsSelected}
            paginationDirection={pagination.direction}
            changePage={paginationVariables => {
              this.handleChangePage(paginationVariables);
            }}
            changeNumberOfRows={this.handleChangeNumberOfRows}
            appliedFilter={filters}
            filter={this.handleFilter}
            refresh={this.handleRefresh}
          />
        )}
      </this.Compose>
    );
  }
}

/**
 * NotificationCategoryListContainer PropTypes
 */
NotificationCategoryListContainer.propTypes = {
  initialFilters: PropTypes.shape({}).isRequired,
};

export default NotificationCategoryListContainer;
export { defaultFilters };
